import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { useUploadService } from '@bingads-webui-campaign-react/smart-component-image';

import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { useDefaultEditorStyle } from '@bingads-webui-campaign-react/smart-component-util';
import { Loading } from '@bingads-webui-campaign-react/smart-component-primitive';
import { useIsSmartPageCustomizedUpdateEnabled } from '@bingads-webui-campaign-react/smart-page-context';
import { ItemPicker } from '@bingads-webui-campaign-react/smart-component-card';

import { SmartUpdateCard } from './smart-update-card';
import { UPDATE_SOURCE, MaxPostsCount } from './constants';

const getStyles = () => ({
  updatesPicker: {
    display: 'block',
    margin: '22px 0',
  },
  connectFacebookDialog: {
    paddingBottom: 32,
    textAlign: 'center',
  },
  connectFacebookMsg: {
    marginTop: 8,
    marginBottom: 16,
  },
  loading: {
    height: '200px',
  },
});

const UnStyledSelectUpdateDialog = ({
  i18n,
  classes,
  onAdd,
  onCancel,
  updates,
  preselectedUpdates,
  pendingDeleteUpdateId,
  isFbSupported,
  isFbInitializing,
  gotoSettings,
}) => {
  const isSmartPageCustomizedUpdateEnabled = useIsSmartPageCustomizedUpdateEnabled();

  const { dialogClassName } = useDefaultEditorStyle();
  const defaultState = {};

  _.each(preselectedUpdates, (update) => {
    defaultState[update] = true;
  });

  const [selectedUpdateIds, setSelectedUpdateIds] = useState(defaultState);
  const [isLoading, setIsLoading] = useState(false);

  const { bulkUploadImagesByUrlsAsync } = useUploadService();

  const onSelectChange = ({ id, isChecked }) => {
    setSelectedUpdateIds(prevState => (
      isChecked ? {
        ...prevState,
        [id]: isChecked,
      } : _.omit(prevState, id)));
  };

  const onAddClicked = () => {
    const selectedUpdates = _.filter(updates, update => !!selectedUpdateIds[update.id]);

    const photosToUpload = _.compact(_.map(
      selectedUpdates,
      update => update.photo && !update.fbPhotoSrc && update.photo
    ));

    bulkUploadImagesByUrlsAsync(photosToUpload)
      .finally((() => {
        setIsLoading(true);
        return () => {
          setIsLoading(false);
        };
      })())
      .then((result) => {
        const imageResult = result;
        let transformedUpdates = [];
        if (_.isArray(result)) {
          let imageIndex = 0;
          transformedUpdates = _.map(selectedUpdates, (selectedUpdate) => {
            const newUpdate = { ...selectedUpdate };
            if (newUpdate.photo && imageIndex < imageResult.length) {
              const { IdString, ImageUrl } = imageResult[imageIndex];
              newUpdate.fbPhotoSrc = newUpdate.photo;
              newUpdate.imageId = IdString;
              newUpdate.photo = ImageUrl;
              newUpdate.source = UPDATE_SOURCE.FACEBOOK;
              imageIndex += 1;
            }
            return newUpdate;
          });
        }

        onAdd({ newSelections: transformedUpdates, imagesToAdd: imageResult });
      });
  };

  const dialogContentProps = {
    className: dialogClassName,
    type: DialogType.close,
    title: i18n.getString(_TL_('Select posts')),
    subText: isFbSupported && (
      isSmartPageCustomizedUpdateEnabled ?
        i18n.getString(_TL_('Add or edit your Facebook updates. You can have up to 10 posts total on your smart page.')) :
        i18n.getString(_TL_('Add and edit your selection of your Facebook posts. You can select up to 10.'))
    ),
    closeButtonAriaLabel: i18n.getString(_TL_('Close')),
  };
  const items = _.filter(updates, update => (
    (_.indexOf(preselectedUpdates, update.id) === -1) && (update.id !== pendingDeleteUpdateId)
  ));

  _.map(items, (item) => {
    _.extend(item, {
      selected: !!selectedUpdateIds[item.id],
      selectable: _.size(selectedUpdateIds) < MaxPostsCount ?
        true : !!selectedUpdateIds[item.id],
    });
  });

  const isAddBtnDisabled = () => {
    const checkedItems = [];
    _.map(_.keys(selectedUpdateIds), (id) => {
      if (selectedUpdateIds[id] && _.indexOf(preselectedUpdates, id) === -1) {
        checkedItems.push(id);
      }
    });
    return _.isEmpty(checkedItems);
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={onCancel}
      dialogContentProps={dialogContentProps}
      minWidth={613}
      modalProps={{ isBlocking: true }}
    >
      {
        (!isFbSupported && !isFbInitializing) ?
          <div className={classes.connectFacebookDialog}>
            <MessageBar isMultiline className={classes.connectFacebookMsg}>
              {i18n.getString(_TL_('Before you can import posts from your Facebook Business Page, you must connect to Facebook.'))}
            </MessageBar>
            <PrimaryButton
              onClick={gotoSettings}
              text={i18n.getString(_TL_('Connect to Facebook'))}
            />
          </div>
          :
          <div>
            {
              isFbInitializing ?
                <Loading className={classes.loading} />
                :
                <Fragment>
                  <UiBlocker
                    blocking={isLoading}
                    loadingText={i18n.getString(_TL_('Adding'))}
                  >
                    <ItemPicker
                      className={classes.updatesPicker}
                      i18n={i18n}
                      ItemComponent={SmartUpdateCard}
                      onSelectChange={onSelectChange}
                      items={items}
                      selectable
                      limit={MaxPostsCount}
                      selectedIds={selectedUpdateIds}
                      noDataLabel={_TL_('There are no Facebook posts to import from the last 90 days.')}
                    />

                    <DialogFooter>
                      <DefaultButton onClick={onCancel} text={i18n.getString(_TL_('Cancel'))} />
                      <PrimaryButton disabled={isAddBtnDisabled()} onClick={onAddClicked} text={i18n.getString(_TL_('Add'))} />
                    </DialogFooter>
                  </UiBlocker>
                </Fragment>
            }
          </div>
      }
    </Dialog>
  );
};

UnStyledSelectUpdateDialog.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    updatesPicker: PropTypes.string.isRequired,
  }).isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  updates: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
    photo: PropTypes.string,
    id: PropTypes.string,
  })).isRequired,
  preselectedUpdates: PropTypes.arrayOf(PropTypes.string).isRequired,
  pendingDeleteUpdateId: PropTypes.string.isRequired,
  isFbInitializing: PropTypes.bool.isRequired,
  isFbSupported: PropTypes.bool.isRequired,
  gotoSettings: PropTypes.func.isRequired,
};

export const SelectUpdateDialog = withDefaultStyles(
  UnStyledSelectUpdateDialog,
  getStyles
);
